import { ManifoldWindow } from 'build/mount';
import { inject, provide } from 'vue';
import { mixins, Options, Vue } from 'vue-class-component';
import { Store } from 'vuex';
import { EthereumNetwork } from '@manifoldxyz/manifold-sdk';
import { CAMPAIGN_STORE } from '@/constants/campaign';

/*
 * @dev:
 * Adding a new prop? Declare it in two places:
 *   1. MWidgetOptionsProps with type and default
 *   2. MWidgetProps (theres an obvious pattern)
 *
 * Optionally, enforce any new prop-dependency rules
 * in the created() hook of MWidgetProvidePropsMixin.
 */
export const MCartWidgetOptionsProps = {
  // Note: These come from original cart.ts
  network: {
    type: Number,
    default: EthereumNetwork.MAINNET
  },
  clientId: {
    type: String,
    default: ''
  },
  grantType: {
    type: String,
    default: 'signature'
  },
  appName: {
    type: String,
    default: ''
  },
  innerClaimText: {
    type: String,
    default: ''
  },
  redemptionSuccessText: {
    type: String,
    default: ''
  },
  productIdOverride: {
    type: Number,
    default: undefined
  },
  cartIconSection: {
    type: String,
    default: 'cart-items,cart-icon-bubble,cart-live-region-text,cart-footer'
  },
  productProperties: {
    type: Array,
    default: ['']
  },
  // Note: These come from AddToCart.vue
  addToCartText: {
    type: String,
    default: ''
  },
  applyDiscountText: {
    type: String,
    default: ''
  },
  connectWalletText: {
    type: String,
    default: ''
  },
  collectionLink: {
    type: String,
    default: ''
  },
  collectionText: {
    type: String,
    default: ''
  },
  openseaCollectionSlug: {
    type: String,
    default: ''
  },
  noEligibleTokensMessage: {
    type: String,
    default: ''
  },
  noEligibleTokensTitle: {
    type: String,
    default: ''
  },
  nftEligibleTitle: {
    type: String,
    default: ''
  },
  nftEligibleMessage: {
    type: String,
    default: ''
  },
  nftAvailableTitle: {
    type: String,
    default: ''
  },
  nftAvailableMessage: {
    type: String,
    default: ''
  },
  reloadPageOnAddToCart: {
    type: Boolean,
    default: undefined
  },
  redirectToCart: {
    type: Boolean,
    default: undefined
  },
  addToCartEmojis: {
    type: Array,
    default: ['👕', '🧢']
  },
  noEmojis: {
    type: Boolean,
    default: undefined
  },
  customStarImage: {
    type: String,
    default: ''
  }
};
export type MCartWidgetOptionsPropsType = keyof typeof MCartWidgetOptionsProps;

/*
 * Defined once and extended so that the inject() and provide() calls made by
 * the two mixins in this file define all of the same things.
 */
export class MCartWidgetProps extends Vue {
  // follow the pattern below when adding new props
  network = MCartWidgetOptionsProps.network.default;
  clientId = MCartWidgetOptionsProps.clientId.default;
  grantType = MCartWidgetOptionsProps.grantType.default;
  appName = MCartWidgetOptionsProps.appName.default;
  innerClaimText = MCartWidgetOptionsProps.innerClaimText.default;
  redemptionSuccessText = MCartWidgetOptionsProps.redemptionSuccessText.default;
  productIdOverride = MCartWidgetOptionsProps.productIdOverride.default;
  cartIconSection = MCartWidgetOptionsProps.cartIconSection.default;
  productProperties = MCartWidgetOptionsProps.productProperties.default;
  addToCartText = MCartWidgetOptionsProps.addToCartText.default;
  applyDiscountText = MCartWidgetOptionsProps.applyDiscountText.default;
  connectWalletText = MCartWidgetOptionsProps.connectWalletText.default;
  collectionLink = MCartWidgetOptionsProps.collectionLink.default;
  collectionText = MCartWidgetOptionsProps.collectionText.default;
  openseaCollectionSlug = MCartWidgetOptionsProps.openseaCollectionSlug.default;
  noEligibleTokensMessage = MCartWidgetOptionsProps.noEligibleTokensMessage.default;
  noEligibleTokensTitle = MCartWidgetOptionsProps.noEligibleTokensTitle.default;
  nftEligibleTitle = MCartWidgetOptionsProps.nftEligibleTitle.default;
  nftEligibleMessage = MCartWidgetOptionsProps.nftEligibleMessage.default;
  nftAvailableTitle = MCartWidgetOptionsProps.nftAvailableTitle.default;
  nftAvailableMessage = MCartWidgetOptionsProps.nftAvailableMessage.default;
  reloadPageOnAddToCart = MCartWidgetOptionsProps.reloadPageOnAddToCart.default;
  redirectToCart = MCartWidgetOptionsProps.redirectToCart.default;
  addToCartEmojis = MCartWidgetOptionsProps.addToCartEmojis.default;
  noEmojis = MCartWidgetOptionsProps.noEmojis.default;
  customStarImage = MCartWidgetOptionsProps.customStarImage.default;
}

/*
 * Used by WalletMixin to inject() back in all parent-provided props as variables
 * Reference: https://vuejs.org/guide/components/provide-inject.html
 */
export class MCartWidgetInjectContext extends mixins(MCartWidgetProps) {
  $store: Store<any>;
  mmid = '';
  loading = true;

  created(): void {
    // get all property definitions declared in MCampaignProvidePropsMixin
    const keys = Object.keys(new MCartWidgetProps({}));
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      (this as any)[key] = inject(key); // eslint-disable-line
    }

    // get additional properties
    this.mmid = inject('mmid') || '';
    this.$store = inject('$store') || (window as Window as ManifoldWindow)[CAMPAIGN_STORE];
  }
}

const CartPropsSavedToStore: string[] = [];

/**
 * Props that might take a computed default value set in MCampaignPropProvider.created().
 * Mapping of Vue prop name to MCampaignPropProvider internal property name.
 *
 * @dev: add the corresponding property to MCampaignPropProvider and the computed logic
 * to MCampaignPropProvider.created().
 */
const OverriddenProps = {
  // Examples from marketplace: -- Not sure if there are any here?
  // maybe redemption token id?
  // version: 'marketplaceVersion',
  // address: 'contractAddress',
  // referralAddress: 'referrer',
} as const;

type OverriddenPropKey = keyof typeof OverriddenProps;

function isOverriddenPropKey(key: string): key is OverriddenPropKey {
  return key in OverriddenProps;
}

/*
 * Used by MWidget.vue to provide() all its props to children components
 * Reference: https://vuejs.org/guide/components/provide-inject.html
 */
@Options({
  props: MCartWidgetOptionsProps
})
export class MCartWidgetPropProvider extends mixins(MCartWidgetProps) {
  popupIsOpen = false;
  htmlOverflowInitial: string | undefined;
  bodyOverflowInitial: string | undefined;
  show = false;
  rewardsFetched = false;
  eligibleRewards: any[] = [];
  hasDiscount = false;
  unrestricted = false;
  // from cart.ts (base class)
  mmid = '';
  loading = true;

  /*
   * Use this created hook to enforce any prop-dependency rules.
   */
  async created() {
    // Take all the additional props potentially passed into the widget
    // and set those corresponding values in the store.
    this.saveProvidedPropsToStore();
    this.provideProps();
  }

  // Private getter for $store -- taken from Jaxon marketplace widget to continue using this.$store everywhere
  get $store(): Store<any> {
    const store = (window as Window as ManifoldWindow)[CAMPAIGN_STORE];
    if (!store) {
      // shouldn't happen...
      throw new Error('MCartWidgetProps.ts: No store found.');
    }
    return store;
  }

  private saveProvidedPropsToStore(): void {
    CartPropsSavedToStore.forEach((propName) => {
      this.$store.dispatch(
        `${this.mmid}/set${propName.charAt(0).toUpperCase()}${propName.substring(1)}`,
        this.getPropValue(propName),
        { root: true }
      );
    });
  }

  /**
   * This function sets the props in a look up table for the children components to get from if needed.
   * This follows the provide()/inject() pattern in Vue -- otherwise known as prop "drilling" so we
   * don't have to pass props down multiple levels.
   */
  private provideProps(): void {
    // define all properties for children who use the MMarketplaceInjectContext mixin
    const keys = Object.keys(new MCartWidgetProps({}));
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      try {
        provide(key, this.getPropValue(key));
      } catch (e) {
        console.log('provide error: ', e);
      }
    }

    // Provide the mmid and $store to children components.
    // (Store them in a lookup table so children classes/components can access them.)
    // provide('mmid', this.mmid);
    provide('mmid', `this.campaignId`);
    provide('$store', this.$store);
  }

  private getPropValue(propName: string): any {
    let value = (this as any)[propName];
    if (isOverriddenPropKey(propName)) {
      value = this[OverriddenProps[propName]];
    }
    return value;
  }
}
